'use client';

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Row,
  Col,
  Space,
  Button,
  Input,
  Select,
  Modal,
  Form,
  Popconfirm,
  InputNumber,
} from 'antd';
import { toast } from 'react-toastify';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useRouter } from 'next/navigation';
import { ItemRecord } from '@/lib/mockItemStore';
import { useAuth } from '@/components/AuthContext';

const { Title, Text } = Typography;

const CATEGORIES = ['Packaging', 'Consumables', 'Warehouse Equipment', 'Raw Materials', 'Accessories', 'Spare Parts', 'Other'];

export default function ItemsPage() {
  const router = useRouter();
  const { user } = useAuth();
  const isManager = user.roles.includes('ADMIN') || user.roles.includes('PURCHASER');

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ItemRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ItemRecord | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (categoryFilter !== 'ALL') params.set('category', categoryFilter);
      const res = await fetch(`/api/items?${params}`);
      const json = await res.json();
      if (json.success) setItems(json.data);
    } catch {
      toast.error('Gagal memuat data katalog item');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleOpenCreate = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ status: 'ACTIVE', defaultPrice: 0 });
    setModalOpen(true);
  };

  const handleOpenEdit = (record: ItemRecord) => {
    setEditingItem(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      category: record.category,
      specs: record.specs,
      uomCode: record.uomCode,
      defaultPrice: record.defaultPrice,
      status: record.status,
      description: record.description,
      minStockLevel: record.minStockLevel,
    });
    setModalOpen(true);
  };

  const handleSubmitForm = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      let res: Response;
      if (editingItem) {
        res = await fetch(`/api/items/${editingItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
      } else {
        res = await fetch('/api/items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
      }

      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Gagal menyimpan item');
      toast.success(json.message || 'Item berhasil disimpan');
      setModalOpen(false);
      fetchItems();
    } catch (err: unknown) {
      if (err instanceof Error) toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      const res = await fetch(`/api/items/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Gagal menghapus item');
      toast.success(json.message);
      fetchItems();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Gagal menghapus item');
    }
  };

  // KPI stats from loaded data
  const activeCount = items.filter((i) => i.status === 'ACTIVE').length;
  const categorySet = new Set(items.map((i) => i.category));

  const columns = [
    {
      title: 'Kode Item',
      dataIndex: 'code',
      key: 'code',
      width: 130,
      render: (c: string, r: ItemRecord) => (
        <span
          style={{ fontWeight: 800, color: '#6366F1', cursor: 'pointer', fontSize: 13 }}
          onClick={() => router.push(`/master/items/${r.id}`)}
        >
          {c}
        </span>
      ),
    },
    {
      title: 'Nama Item / Material',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, r: ItemRecord) => (
        <div>
          <span
            style={{ fontWeight: 700, color: '#0F172A', fontSize: 13, cursor: 'pointer' }}
            onClick={() => router.push(`/master/items/${r.id}`)}
          >
            {name}
          </span>
          {r.specs && (
            <div style={{ color: '#64748B', fontSize: 11, marginTop: 2, maxWidth: 400 }}>
              {r.specs}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Kategori',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      render: (cat: string) => {
        const colors: Record<string, string> = {
          Packaging: 'blue', Consumables: 'orange', 'Warehouse Equipment': 'purple',
          'Raw Materials': 'green', Accessories: 'cyan', 'Spare Parts': 'volcano',
        };
        return (
          <Tag color={colors[cat] || 'default'} style={{ borderRadius: 6, fontSize: 11 }}>
            {cat}
          </Tag>
        );
      },
    },
    {
      title: 'Satuan',
      dataIndex: 'uomCode',
      key: 'uomCode',
      width: 90,
      render: (uom: string) => <Tag style={{ borderRadius: 4, fontWeight: 600 }}>{uom}</Tag>,
    },
    {
      title: 'Harga Default',
      dataIndex: 'defaultPrice',
      key: 'defaultPrice',
      align: 'right' as const,
      width: 150,
      render: (price: number) => (
        <span style={{ fontWeight: 700, color: '#059669' }}>
          Rp {price.toLocaleString('id-ID')}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s: string) => (
        <Tag
          color={s === 'ACTIVE' ? 'success' : 'default'}
          style={{ borderRadius: 20, fontWeight: 600, fontSize: 11 }}
        >
          {s === 'ACTIVE' ? 'Aktif' : 'Nonaktif'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 170,
      align: 'right' as const,
      render: (_: unknown, r: ItemRecord) => (
        <Space size="small">
          <Button
            size="small"
            type="default"
            icon={<EyeOutlined />}
            onClick={() => router.push(`/master/items/${r.id}`)}
            style={{ borderRadius: 6, color: '#6366F1', borderColor: '#C7D2FE', fontSize: 12 }}
          >
            Detail
          </Button>
          {isManager && (
            <>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleOpenEdit(r)}
                style={{ borderRadius: 6 }}
              />
              <Popconfirm
                title="Hapus Item Katalog"
                description={`Hapus '${r.name}'? Item dengan sisa stok inventaris tidak dapat dihapus.`}
                onConfirm={() => handleDelete(r.id, r.name)}
                okText="Ya, Hapus"
                cancelText="Batal"
                okButtonProps={{ danger: true }}
              >
                <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Title level={3} style={{ margin: 0, color: '#0F172A', fontWeight: 800 }}>
              Item Master Catalog
            </Title>
            <span
              style={{
                fontSize: 12, fontWeight: 600, color: '#0369A1',
                background: '#E0F2FE', border: '1px solid #BAE6FD',
                padding: '3px 10px', borderRadius: 20,
              }}
            >
              Katalog Pengadaan
            </span>
          </div>
          <Text type="secondary" style={{ fontSize: 13, marginTop: 4, display: 'block' }}>
            Daftar master item dan material yang dapat dipesan melalui Purchase Request dan Purchase Order.
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchItems} style={{ borderRadius: 10 }}>
            Segarkan
          </Button>
          {isManager && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreate}
              style={{
                borderRadius: 10,
                background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                fontWeight: 600,
              }}
            >
              Tambah Item Baru
            </Button>
          )}
        </Space>
      </div>

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Item Terdaftar', value: `${items.length} Item`, sub: 'Seluruh katalog pengadaan', color: '#EEF2FF', border: '#E0E7FF', text: '#1E1B4B' },
          { label: 'Item Aktif (Dapat Dipesan)', value: `${activeCount} Item`, sub: 'Tersedia untuk PR & PO', color: '#F0FDF4', border: '#BBF7D0', text: '#064E3B' },
          { label: 'Jumlah Kategori', value: `${categorySet.size} Kategori`, sub: 'Pengelompokan material', color: '#FFF7ED', border: '#FED7AA', text: '#7C2D12' },
        ].map((kpi) => (
          <Col xs={24} sm={8} key={kpi.label}>
            <div
              style={{
                background: `linear-gradient(135deg, #FFFFFF 0%, ${kpi.color} 100%)`,
                padding: '18px 20px', borderRadius: 14,
                border: `1px solid ${kpi.border}`,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>
                {kpi.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: kpi.text, marginTop: 4 }}>
                {kpi.value}
              </div>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>{kpi.sub}</div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Table Card */}
      <Card
        style={{ borderRadius: 16, border: '1px solid #F1F5F9' }}
        styles={{ body: { padding: '18px 20px' } }}
      >
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={14} md={12}>
            <Input
              placeholder="Cari berdasarkan Kode, Nama Item, atau Spesifikasi..."
              prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 10 }}
            />
          </Col>
          <Col xs={24} sm={10} md={8}>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              style={{ width: '100%' }}
              options={[
                { value: 'ALL', label: 'Semua Kategori' },
                ...CATEGORIES.map((c) => ({ value: c, label: c })),
              ]}
            />
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ pageSize: 15 }}
          size="middle"
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editingItem ? `Edit Item: ${editingItem.code}` : 'Tambah Item Master Baru'}
        open={modalOpen}
        onOk={handleSubmitForm}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        okText={editingItem ? 'Simpan Perubahan' : 'Tambahkan Item'}
        cancelText="Batal"
        width={640}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={9}>
              <Form.Item name="code" label="Kode Item" rules={[{ required: true }]}>
                <Input placeholder="Contoh: ITM-009" style={{ borderRadius: 8, textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col span={15}>
              <Form.Item name="name" label="Nama Item / Material" rules={[{ required: true }]}>
                <Input placeholder="Nama lengkap item" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Kategori" rules={[{ required: true }]}>
                <Select
                  options={CATEGORIES.map((c) => ({ value: c, label: c }))}
                  placeholder="Pilih kategori..."
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="uomCode" label="Satuan (UoM)" rules={[{ required: true }]}>
                <Select
                  options={['PCS', 'ROLL', 'METER', 'KG', 'BOX', 'YARD', 'SET', 'LITER'].map((u) => ({
                    value: u, label: u,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="status" label="Status">
                <Select
                  options={[
                    { value: 'ACTIVE', label: 'Aktif' },
                    { value: 'INACTIVE', label: 'Nonaktif' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="specs" label="Spesifikasi Teknis">
            <Input placeholder="Contoh: Double wall, kraft paper 275gsm" style={{ borderRadius: 8 }} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="defaultPrice" label="Harga Estimasi Default (Rp)">
                <InputNumber
                  style={{ width: '100%', borderRadius: 8 }}
                  min={0}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="minStockLevel" label="Minimum Stok Peringatan">
                <InputNumber style={{ width: '100%', borderRadius: 8 }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Keterangan Tambahan">
            <Input.TextArea rows={2} placeholder="Catatan penggunaan atau informasi tambahan..." style={{ borderRadius: 8 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
